import { AxiosPromise } from "axios";
import React from "react";
import { useCustomQuery } from "utilities/hookutils";
import { FormSection, InPageEditableSectionField } from "../types";
import { AddTranslationModal } from "./AddTranslationModal";
import { DetailSectionTemplateWithTranslationLayout } from "./DetailSectionTemplateWithTranslationLayout";
import DeleteDialog from "components/deleteDialog";

type FieldKeyPrefix = server.dto.Languages | "general";

type FormState = Record<server.dto.Languages, Record<string, any>>

interface DetailSectionTemplateWithTranslationProps<T> {
  onValueChanged: (key: string, value: any) => void;
  queryKey: string;
  isInPageEditable: boolean;
  topSection: FormSection;
  listApi: () => AxiosPromise<server.dto.ApiResponseWrapper<T[]>>;
  addTranslationApi: (language: server.dto.Languages) => AxiosPromise<server.dto.ApiResponse>;
  removeTranslationApi: (language: server.dto.Languages) => AxiosPromise<server.dto.ApiResponse>;
  renderFieldsForTranslation: (language: server.dto.Languages, formStateForTranslation: Record<string, any>) => Array<InPageEditableSectionField>;
}

// TODO: We need a loading state here for list, delete and add. Also, we should do error handling for them
export const DetailSectionTemplateWithTranslation = <T extends server.dto.ITranslation>(props: DetailSectionTemplateWithTranslationProps<T>) => {
  const [isAddingTranslation, setIsAddingTranslation] = React.useState(false);
  const [isDeletingTranslation, setIsDeletingTranslation] = React.useState<server.dto.Languages | null>(null);
  const [formState, setFormState] = React.useState<FormState>(null);

  const api = useCustomQuery({
    queryFn: () => props.listApi(),
    queryKey: ["listTranslations", props.queryKey],
    onSuccess: (res) => {
      setFormState(
        res.data.data.reduce((acc, item) => {
          acc[item.language] = { ...item };
          return acc;
        }, {} as FormState)
      );
    }
  })

  const onTranslationAdded = () => {
    setIsAddingTranslation(false);
    api.refetch();
  }

  const onDeleteClicked = (language: server.dto.Languages) => {
    if (api.data.data.data.length === 1) {
      window.displayGenericModal(RESX.GeneralWarnings.Warning, RESX.MessageTemplate.CannotDeleteLastTranslation, "tiny", "red");
      return;
    }
    setIsDeletingTranslation(language);
  }

  const deleteTranslation = async (language: server.dto.Languages) => {
    const resp = await props.removeTranslationApi(language);
    await api.refetch();
    setIsDeletingTranslation(null);
    return resp;
  }

  const onValueChanged = (prefix: FieldKeyPrefix, key: string, value: any) => {
    if (prefix === "general") {
      // Propagate general changes up one level
      props.onValueChanged(key, value);
    } else {
      // Update the local translation state value
      setFormState(prev => ({
        ...prev,
        [prefix]: {
          ...prev[prefix],
          [key]: value
        }
      }));
    }
  }

  const items = api.data?.data.data || [];

  return <>
    {isAddingTranslation && <AddTranslationModal
      existingTranslations={items.map(i => i.language)}
      onClose={() => setIsAddingTranslation(false)}
      saveApi={props.addTranslationApi}
      onTranslationAdded={onTranslationAdded}
    />}
    {isDeletingTranslation != null && <DeleteDialog
      spinForever
      isOpen
      onDelete={() => deleteTranslation(isDeletingTranslation)}
      handleClose={() => { setIsDeletingTranslation(null); }}
      message={RESX.GeneralWarnings.DeleteSure}
    />}
    <DetailSectionTemplateWithTranslationLayout
      isInPageEditable={props.isInPageEditable}
      isLoading={api.isInitialLoading}
      topSection={{
        ...props.topSection,
        fields: props.topSection.fields.map(field => ({ ...field, key: `general:${field.key}` }))
      }}
      items={items}
      onAdd={() => setIsAddingTranslation(true)}
      onDelete={onDeleteClicked}
      onValueChanged={(key: string, value: any) => {
        const [prefix, fieldKey] = key.split(":") as [FieldKeyPrefix, string];
        onValueChanged(prefix, fieldKey, value);
      }}
      renderFieldsForTranslation={(item) =>
        formState != null
          ? props.renderFieldsForTranslation(item.language, formState[item.language]).map(field => ({ ...field, key: `${item.language}:${field.key}` }))
          : []
      }
    />
  </>
}